import { IStage } from '@forwardflow/store/onboardingStoreV2'
import {
   IAvailabilityDay,
   ICampaignConfiguration,
   IStageMapGroup,
   ITempStageGroup,
   TOnboardingStep,
} from '@forwardflow/types/onboarding'

export const initialAvailabilityDays: IAvailabilityDay[] = [
   {
      day: 'Monday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Tuesday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Wednesday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Thursday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Friday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Saturday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Sunday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: false,
   },
]

export const OPERATOR_EMAILS = new Set(['operator@forward-flow.com'])

export const ONBOARDING_DUMMY_ORGANIZATION_ID =
   'f5f26377cff4a4ed9b3b07ba7c4e8a18'

export const AGENT_ONBOARDING_STEPS: TOnboardingStep[] = [
   'calendar-connection',
   'getting-started',
]

/**
 * The onboarding wizard, trimmed to what a customer must decide before the
 * dashboard. Everything else (calendar, hours, business details, CNAM,
 * campaign setup, voice, inbox app) is configured later from the app.
 *
 * Plan selection stays first because it creates the organization the
 * Follow Up Boss step needs.
 */
export const ONBOARDING_STEPS: TOnboardingStep[] = [
   'plan-selection',
   'fub-connect',
   'payment-details',
   'team-invitation',
   'phone-number-configuration',
   'stage-mapping-explained',
]

/** Steps removed from the wizard on 2026-10-12; components kept on disk. */
export const RETIRED_ONBOARDING_STEPS: TOnboardingStep[] = [
   'calendar-connection',
   'leads-availability',
   'user-availability',
   'business-details',
   'presentation-step',
   'setup-cname',
   'summary-before-stage-mapping',
   'dnd-stage-mapping',
   'campaign-configuration',
   'voice-agent-configuration',
   'install-inbox-app',
]

export const STEP_LABELS: Record<TOnboardingStep, string> = {
   'assistant-name': 'Assistant Name',
   'plan-selection': 'Plan Selection',
   'fub-connect': 'FUB Connect',
   'payment-details': 'Payment Details',
   'team-invitation': 'Team Invitation',
   'leads-availability': 'Leads Availability',
   'user-availability': 'User Availability',
   'business-details': 'Business Details',
   'presentation-step': 'Presentation Step',
   'setup-cname': 'Setup CNAM',
   'summary-before-stage-mapping': 'Summary Before Stage Mapping',
   'stage-mapping-nurture-stages': 'Stage Mapping Nurture Stages',
   'stage-mapping-appointment': 'Stage Mapping Appointment',
   'stage-mapping-stop-stages': 'Stage Mapping Stop Stages',
   'stage-mapping-definition': 'Stage Mapping Definition',
   'stage-mapping-new-leads': 'Stage Mapping New Leads',
   'campaign-configuration': 'Campaign Setup',
   'voice-agent-configuration': 'Voice Agent',
   'phone-number-configuration': 'Phone Number Selection',
   'install-inbox-app': 'Install Inbox App',
   'dnd-stage-mapping': 'Stage Mapping',
   'stage-mapping-explained': 'Stage Mapping',
   'calendar-connection': 'Calendar Connection',
   'getting-started': 'Getting Started',
}

const EMPTY_STAGE: IStage = {
   id: '',
   name: '',
   description: '',
   type: 'stage',
}

export const initialTempStageGroups: ITempStageGroup[] = [
   {
      groupId: 'ff-nurturing',
      parentGroupId: null,
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-appointment-set',
      parentGroupId: null,
      type: 'appointment-field',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-optional-mappings',
      parentGroupId: null,
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-appointment-missed',
      parentGroupId: null,
      type: 'no-criteria',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-do-not-contact',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-active-client',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-under-contract',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-closed-client',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-has-agent',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-other-stop',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
]

export const initialStageMappingsGroups: IStageMapGroup[] = [
   {
      group: 'ff-nurturing',
      timeline: '0-3 Months',
      isMandatory: true,
      allowMultiple: false,
      stages: [],
      tags: [],
   },
   {
      type: 'appointment-field',
      group: 'ff-appointment-set',
      stages: [],
      isMandatory: true,
      tags: [],
      allowMultiple: true,
   },
   {
      group: 'ff-appointment-missed',
      stages: [],
      tags: [],
      allowMultiple: false,
   },
   {
      group: 'ff-stopped',
      types: [
         {
            key: 'ff-active-client',
            stages: [],
            tags: [],
            allowMultiple: true,
         },
         {
            key: 'ff-under-contract',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: true,
         },
         {
            key: 'ff-closed-client',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: false,
         },
         {
            key: 'ff-do-not-contact',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: false,
         },

         {
            key: 'ff-has-agent',
            stages: [],
            tags: [],
            allowMultiple: false,
         },
         {
            key: 'ff-other-stop',
            stages: [],
            tags: [],
            allowMultiple: true,
         },
      ],
   },
]

export const initialCampaignConfiguration: ICampaignConfiguration = {
   usps: null,
   source: '',
   status: 'draft',
   ai_assistants: [],
   campaign_name: '',
   phone_numbers: [],
   campaign_filters: {
      eligible_criteria: [],
   },
   first_to_contact: {
      buffer_time: 0,
   },
   is_demo_campaign: null,
   qualifying_questions: null,
   appointment_timeframes: null,
   discovery_people_limit: 0,
   area_codes: [],
   lead_type_filter: ['buyer', 'seller'],
}

// A partial list of U.S. area codes (not exhaustive):
export const US_AREA_CODES = [
   '201',
   '202',
   '203',
   '205',
   '206',
   '207',
   '208',
   '209',
   '210',
   '212',
   '213',
   '214',
   '215',
   '216',
   '217',
   '218',
   '219',
   '224',
   '225',
   '228',
   '229',
   '231',
   '234',
   '239',
   '240',
   '248',
   '251',
   '252',
   '253',
   '254',
   '256',
   '260',
   '262',
   '267',
   '269',
   '270',
   '272',
   '274',
   '276',
   '281',
   '301',
   '302',
   '303',
   '304',
   '305',
   '307',
   '308',
   '309',
   '310',
   '312',
   '313',
   '314',
   '315',
   '316',
   '317',
   '318',
   '319',
   '320',
   '321',
   '323',
   '325',
   '327',
   '330',
   '331',
   '334',
   '336',
   '337',
   '339',
   '346',
   '347',
   '351',
   '352',
   '360',
   '361',
   '364',
   '380',
   '385',
   '386',
   '401',
   '402',
   '404',
   '405',
   '406',
   '407',
   '408',
   '409',
   '410',
   '412',
   '413',
   '414',
   '415',
   '417',
   '419',
   '423',
   '424',
   '425',
   '430',
   '432',
   '434',
   '435',
   '440',
   '442',
   '443',
   '447',
   '458',
   '463',
   '464',
   '470',
   '475',
   '478',
   '479',
   '480',
   '484',
   '501',
   '502',
   '503',
   '504',
   '505',
   '507',
   '508',
   '509',
   '510',
   '512',
   '513',
   '515',
   '516',
   '517',
   '518',
   '520',
   '530',
   '531',
   '534',
   '539',
   '540',
   '541',
   '551',
   '559',
   '561',
   '562',
   '563',
   '564',
   '567',
   '570',
   '571',
   '573',
   '574',
   '575',
   '580',
   '585',
   '586',
   '601',
   '602',
   '603',
   '605',
   '606',
   '607',
   '608',
   '609',
   '610',
   '612',
   '614',
   '615',
   '616',
   '617',
   '618',
   '619',
   '620',
   '623',
   '626',
   '628',
   '629',
   '630',
   '631',
   '636',
   '641',
   '646',
   '650',
   '651',
   '657',
   '660',
   '661',
   '662',
   '667',
   '669',
   '678',
   '679',
   '681',
   '682',
   '701',
   '702',
   '703',
   '704',
   '706',
   '707',
   '708',
   '712',
   '713',
   '714',
   '715',
   '716',
   '717',
   '718',
   '719',
   '720',
   '724',
   '725',
   '727',
   '730',
   '731',
   '732',
   '734',
   '737',
   '740',
   '747',
   '754',
   '757',
   '760',
   '762',
   '763',
   '765',
   '769',
   '770',
   '772',
   '773',
   '774',
   '775',
   '779',
   '781',
   '785',
   '786',
   '801',
   '802',
   '803',
   '804',
   '805',
   '806',
   '808',
   '810',
   '812',
   '813',
   '814',
   '815',
   '816',
   '817',
   '818',
   '828',
   '830',
   '831',
   '832',
   '838',
   '839',
   '840',
   '843',
   '845',
   '847',
   '848',
   '850',
   '854',
   '856',
   '857',
   '858',
   '859',
   '860',
   '862',
   '863',
   '864',
   '865',
   '870',
   '872',
   '878',
   '901',
   '903',
   '904',
   '906',
   '907',
   '908',
   '909',
   '910',
   '912',
   '913',
   '914',
   '915',
   '916',
   '917',
   '918',
   '919',
   '920',
   '925',
   '928',
   '929',
   '930',
   '931',
   '934',
   '936',
   '937',
   '938',
   '940',
   '941',
   '945',
   '947',
   '949',
   '951',
   '952',
   '954',
   '956',
   '959',
   '970',
   '971',
   '972',
   '973',
   '975',
   '978',
   '979',
   '980',
   '984',
   '985',
   '989',
]

export const EAC_NEW_LEAD = 0.62
export const EAC_COLD_LEAD = 0.44
export const BASE_SEAT_COST = 97
export const ADDITIONAL_SEAT_COST = 50
export const MIN_TOP_UP = 200
